import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'
import { fetchDispatch } from 'data/helpers'
import { fetchSSE } from 'data/sseHelpers'

// Action Types
export const SEND_MESSAGE_LOADING = 'aiCandidates/SEND_MESSAGE_LOADING'
export const SEND_MESSAGE_SUCCESS = 'aiCandidates/SEND_MESSAGE_SUCCESS'
export const SEND_MESSAGE_FAILURE = 'aiCandidates/SEND_MESSAGE_FAILURE'

export const ADD_CHAT_MESSAGE = 'aiCandidates/ADD_CHAT_MESSAGE'
export const CLEAR_CHAT_HISTORY = 'aiCandidates/CLEAR_CHAT_HISTORY'
export const SET_AI_TYPING = 'aiCandidates/SET_AI_TYPING'
export const RESET_CONVERSATION = 'aiCandidates/RESET_CONVERSATION'
export const SET_MESSAGE_FEEDBACK = 'aiCandidates/SET_MESSAGE_FEEDBACK'
export const SET_CANDIDATE_FEEDBACK = 'aiCandidates/SET_CANDIDATE_FEEDBACK'
export const SET_MOBILE_DRAWER_STATE = 'aiCandidates/SET_MOBILE_DRAWER_STATE'
export const SET_ACTIVE_SEARCH_SOURCE = 'aiCandidates/SET_ACTIVE_SEARCH_SOURCE'
export const ADD_SYSTEM_MESSAGE = 'aiCandidates/ADD_SYSTEM_MESSAGE'
export const SET_TYPING_STATUS = 'aiCandidates/SET_TYPING_STATUS'

// Action Creators
export const sendMessageLoading = () => ({ type: SEND_MESSAGE_LOADING })
export const sendMessageSuccess = payload => ({ type: SEND_MESSAGE_SUCCESS, payload })
export const sendMessageFailure = payload => ({ type: SEND_MESSAGE_FAILURE, payload })

export const addChatMessage = payload => ({ type: ADD_CHAT_MESSAGE, payload })
export const clearChatHistory = () => ({ type: CLEAR_CHAT_HISTORY })
export const setAiTyping = payload => ({ type: SET_AI_TYPING, payload })
export const resetConversation = () => ({ type: RESET_CONVERSATION })
export const setMessageFeedback = payload => ({ type: SET_MESSAGE_FEEDBACK, payload })
export const setCandidateFeedback = payload => ({ type: SET_CANDIDATE_FEEDBACK, payload })
export const setMobileDrawerState = payload => ({ type: SET_MOBILE_DRAWER_STATE, payload })
export const setActiveSearchSource = payload => ({ type: SET_ACTIVE_SEARCH_SOURCE, payload })
export const addSystemMessage = payload => ({ type: ADD_SYSTEM_MESSAGE, payload })
export const setTypingStatus = payload => ({ type: SET_TYPING_STATUS, payload })

const CHAT_RESPONSE_TIMEOUT_MS = 45000
const TRANSIENT_RETRY_DELAY_MS = 2000

const isTransientError = (error) =>
  error?.response?.status === 502 || error?.response?.status === 503

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

let activeAbortController = null

const handleChatTimeout = (dispatch) => {
  activeAbortController = null
  dispatch(addChatMessage({
    type: 'ai',
    message: "Sorry, that took too long. Please try again!",
    metadata: { isSystem: true }
  }))
  dispatch(sendMessageFailure(null))
  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const handleChatResponse = (dispatch, response) => {
  activeAbortController = null
  dispatch(sendMessageSuccess(response))

  if (response.content) {
    dispatch(addChatMessage({
      type: 'ai',
      message: response.content,
      metadata: {
        chatbotMessageId: response.chatbotMessageId,
        candidatesCount: response.rankedCandidates?.length || 0,
      }
    }))
  } else if (response.rankedCandidates?.length > 0) {
    dispatch(addChatMessage({
      type: 'ai',
      message: `I found ${response.rankedCandidates.length} candidate${response.rankedCandidates.length !== 1 ? 's' : ''} matching your search.`,
      metadata: {
        chatbotMessageId: response.chatbotMessageId,
        candidatesCount: response.rankedCandidates.length,
      }
    }))
  } else {
    dispatch(addChatMessage({
      type: 'ai',
      message: "I couldn't find any candidates matching your criteria. Try broadening your search or adjusting your filters.",
      metadata: {
        chatbotMessageId: response.chatbotMessageId,
        candidatesCount: 0,
      }
    }))
  }

  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const handleChatError = (dispatch, error) => {
  activeAbortController = null
  dispatch(
    showSnackbar(
      'Sorry! We encountered an error. Please try again.',
      snackbarTypes.ERROR,
    ),
  )
  dispatch(sendMessageFailure(error?.response))
  dispatch(setTypingStatus(null))
  dispatch(setAiTyping(false))
}

const sendViaSSE = (dispatch, message, conversationId, jobId) => {
  const abortController = new AbortController()

  if (activeAbortController) {
    activeAbortController.abort()
  }
  activeAbortController = abortController
  let handled = false

  let timeoutId = setTimeout(() => {
    if (handled) return
    handled = true
    abortController.abort()
    handleChatTimeout(dispatch)
  }, CHAT_RESPONSE_TIMEOUT_MS)

  return new Promise((resolve, reject) => {
    const { promise } = fetchSSE({
      path: '/api/candidate_listing_chatbot/message/stream',
      values: { content: message, conversationId, jobId },
      abortController,
      onProgress: (data) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          if (handled) return
          handled = true
          abortController.abort()
          handleChatTimeout(dispatch)
        }, CHAT_RESPONSE_TIMEOUT_MS)
        dispatch(setTypingStatus(data))
      },
      onComplete: (data) => {
        if (handled) return
        handled = true
        clearTimeout(timeoutId)
        handleChatResponse(dispatch, data)
        resolve()
      },
      onError: (data) => {
        if (handled) return
        handled = true
        clearTimeout(timeoutId)
        handleChatError(dispatch, new Error(data?.message || 'Stream error'))
        resolve()
      },
    })
    promise.catch(error => {
      clearTimeout(timeoutId)
      reject(error)
    })
  })
}

const sendViaFetch = async (dispatch, message, conversationId, jobId) => {
  const abortController = new AbortController()
  const timeoutId = setTimeout(() => {
    abortController.abort()
    handleChatTimeout(dispatch)
  }, CHAT_RESPONSE_TIMEOUT_MS)

  const response = await fetchDispatch({
    path: '/api/candidate_listing_chatbot/message',
    method: 'POST',
    values: { content: message, conversationId, jobId },
    signal: abortController.signal,
  })
  clearTimeout(timeoutId)
  handleChatResponse(dispatch, response)
}

// Thunk Actions
export const sendChatMessage = ({ message, conversationId, jobId }) => {
  return async dispatch => {
    dispatch(sendMessageLoading())
    dispatch(setAiTyping(true))
    dispatch(setTypingStatus(null))

    try {
      await sendViaSSE(dispatch, message, conversationId, jobId)
    } catch (sseError) {
      if (sseError?.name === 'AbortError') return
      try {
        await sendViaFetch(dispatch, message, conversationId, jobId)
      } catch (error) {
        if (error?.name === 'AbortError') return
        if (isTransientError(error)) {
          try {
            await delay(TRANSIENT_RETRY_DELAY_MS)
            await sendViaFetch(dispatch, message, conversationId, jobId)
          } catch (retryError) {
            if (retryError?.name === 'AbortError') return
            handleChatError(dispatch, retryError)
          }
        } else {
          handleChatError(dispatch, error)
        }
      }
    }
  }
}

export const stopChatRequest = () => {
  return dispatch => {
    if (activeAbortController) {
      activeAbortController.abort()
      activeAbortController = null
    }
    dispatch(setTypingStatus(null))
    dispatch(setAiTyping(false))
    dispatch(sendMessageFailure(null))
    dispatch(addChatMessage({
      type: 'ai',
      message: 'Request stopped.',
      metadata: { isSystem: true }
    }))
  }
}

export const clearConversation = () => {
  return dispatch => {
    if (activeAbortController) {
      activeAbortController.abort()
      activeAbortController = null
    }
    dispatch(resetConversation())
  }
}

const FEEDBACK_REASON_VALUES = {
  didnt_understand: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  wrong_experience: 4,
  irrelevant_candidates: 5,
  missing_criteria: 6,
  inaccurate: 7,
  not_helpful: 8,
  relevant_candidates: 9,
  right_specialty: 10,
  good_location: 11,
  right_experience: 12,
  understood_needs: 13,
  helpful_response: 14,
  other: 15,
}

const VOTE_VALUES = { up: 1, down: 0 }

export const submitMessageFeedback = ({ chatbotMessageId, conversationId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/candidate_listing_chatbot/feedback',
    values: {
      conversationId,
      chatbotMessageId,
      vote: VOTE_VALUES[vote],
      messageReasons: reasons.map(r => FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

const CANDIDATE_FEEDBACK_REASON_VALUES = {
  inaccurate_match: 1,
  wrong_specialty: 2,
  wrong_location: 3,
  wrong_experience: 4,
  missing_requirements: 5,
  justification_unclear: 6,
  accurate_match: 7,
  good_specialty_fit: 8,
  location_right: 9,
  experience_matches: 10,
  criteria_match: 11,
  other: 12,
}

export const submitCandidateFeedback = ({ candidateId, conversationId, chatbotMessageId, vote, reasons, comment }) => {
  fetchDispatch({
    path: '/api/candidate_listing_chatbot/candidate_feedback',
    values: {
      conversationId,
      chatbotMessageId,
      candidateId,
      vote: VOTE_VALUES[vote],
      jobReasons: reasons.map(r => CANDIDATE_FEEDBACK_REASON_VALUES[r]).filter(Boolean),
      comment,
    },
    method: 'POST',
  }).catch(() => {})
}

// Initial State
const initialState = {
  loading: false,
  conversationId: null,
  rankedCandidates: [],
  candidateListingArguments: null,
  resolvedFilters: null,
  chatHistory: [],
  isAiTyping: false,
  mobileDrawerState: 'minimized',
  activeSearchSource: 'traditional',
  suggestedFollowUps: [],
  typingStatus: null,
}

// Reducer
export const aiCandidateSearchReducer = (state = initialState, action) => {
  switch (action.type) {
    case SEND_MESSAGE_LOADING:
      return { ...state, loading: true }

    case SEND_MESSAGE_SUCCESS: {
      const hasNewCandidates = action.payload.rankedCandidates != null
      return {
        ...state,
        loading: false,
        conversationId: action.payload.conversationId || state.conversationId,
        rankedCandidates: hasNewCandidates
          ? action.payload.rankedCandidates.map(rc => ({ ...rc, chatbotMessageId: action.payload.chatbotMessageId }))
          : state.rankedCandidates,
        candidateListingArguments: action.payload.candidateListingArguments ?? state.candidateListingArguments,
        resolvedFilters: action.payload.resolvedFilters ?? state.resolvedFilters,
        suggestedFollowUps: action.payload.suggestedFollowUps || [],
        activeSearchSource: hasNewCandidates ? 'ai' : state.activeSearchSource,
        typingStatus: null,
      }
    }

    case SEND_MESSAGE_FAILURE:
      return { ...state, loading: false, typingStatus: null }

    case SET_MESSAGE_FEEDBACK:
      return {
        ...state,
        chatHistory: state.chatHistory.map(msg =>
          msg.id === action.payload.messageId
            ? { ...msg, feedback: { ...action.payload } }
            : msg
        ),
      }

    case SET_CANDIDATE_FEEDBACK:
      return {
        ...state,
        rankedCandidates: state.rankedCandidates.map(rankedCandidate =>
          rankedCandidate.candidate?.id === action.payload.candidateId
            ? { ...rankedCandidate, feedback: { ...action.payload } }
            : rankedCandidate
        ),
      }

    case ADD_CHAT_MESSAGE:
      return {
        ...state,
        chatHistory: [...state.chatHistory, {
          id: Date.now() + Math.random(),
          type: action.payload.type,
          message: action.payload.message,
          timestamp: new Date().toISOString(),
          ...action.payload.metadata
        }]
      }

    case CLEAR_CHAT_HISTORY:
      return {
        ...state,
        chatHistory: []
      }

    case SET_AI_TYPING:
      return {
        ...state,
        isAiTyping: action.payload
      }

    case RESET_CONVERSATION:
      return {
        ...initialState,
        mobileDrawerState: state.mobileDrawerState,
        activeSearchSource: 'traditional',
      }

    case SET_MOBILE_DRAWER_STATE:
      return {
        ...state,
        mobileDrawerState: action.payload
      }

    case SET_ACTIVE_SEARCH_SOURCE:
      return {
        ...state,
        activeSearchSource: action.payload,
      }

    case ADD_SYSTEM_MESSAGE:
      return {
        ...state,
        chatHistory: [...state.chatHistory, {
          id: Date.now() + Math.random(),
          type: 'system',
          isSystem: true,
          message: action.payload,
          timestamp: new Date().toISOString(),
        }],
      }

    case SET_TYPING_STATUS:
      return {
        ...state,
        typingStatus: action.payload,
      }

    default:
      return state
  }
}
